import React, { useState, useEffect } from 'react';
import {
    FaHeart, FaRegHeart, FaComment, FaShare, FaEllipsisH, FaStar,
    FaRegStar, FaMapMarkerAlt, FaDownload, FaSyncAlt, FaTimes, FaMap
} from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from '../../pages/Account.module.css';
import pb from '../../lib/pocketbase';
import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const PhotoFeed = ({ photos, user, favorites, toggleFavorite, isDiaporama, closeDiaporama }) => {
    const [likes, setLikes] = useState({});
    const [likedPosts, setLikedPosts] = useState({});
    const [index, setIndex] = useState(-1);
    const [mapPhoto, setMapPhoto] = useState(null);

    // Fix Leaflet icons
    useEffect(() => {
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        });
    }, []);

    useEffect(() => {
        const initialLikes = {};
        photos.forEach(photo => {
            initialLikes[photo.id] = Math.floor(Math.random() * 8) + 3;
        });
        setLikes(initialLikes);

        if (user?.id) {
            const stored = localStorage.getItem(`likes_${user.id}`);
            if (stored) setLikedPosts(JSON.parse(stored));
        }
    }, [photos, user?.id]);

    const handleLike = (photoId) => {
        const isLiked = likedPosts[photoId];
        const newLikes = { ...likedPosts, [photoId]: !isLiked };
        setLikedPosts(newLikes);
        localStorage.setItem(`likes_${user.id}`, JSON.stringify(newLikes));
        setLikes(prev => ({ ...prev, [photoId]: isLiked ? prev[photoId] - 1 : prev[photoId] + 1 }));
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        if (seconds < 3600) return Math.floor(seconds / 60) + " min";
        if (seconds < 86400) return Math.floor(seconds / 3600) + " h";
        return Math.floor(seconds / 86400) + " j";
    };

    const slides = photos.map(p => ({
        src: p.url || pb.files.getURL(p, p.file),
        title: p.caption,
        description: `Posté le ${new Date(p.created).toLocaleDateString()}`
    }));

    const getMilestone = (caption = "") => {
        const lower = caption.toLowerCase();
        if (lower.includes("fondation")) return "FONDATIONS";
        if (lower.includes("ossature") || lower.includes("murs")) return "OSSATURE BOIS";
        if (lower.includes("toiture") || lower.includes("hors d'eau")) return "HORS D'EAU";
        if (lower.includes("menuiserie") || lower.includes("hors d'air")) return "HORS D'AIR";
        return null;
    };

    const parseCoords = (loc) => {
        if (!loc || typeof loc !== 'string') return null;
        const parts = loc.split(',').map(s => s.trim());
        if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lng)) return [lat, lng];
        }
        return null;
    };

    return (
        <div className="photo-feed-container" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {photos.map((photo, i) => {
                const milestone = getMilestone(photo.caption);
                const isFav = favorites?.includes(photo.id);

                return (
                    <div key={photo.id} className="feed-card" style={{
                        background: 'var(--color-surface, #fff)',
                        borderRadius: '24px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
                        marginBottom: '2.5rem',
                        overflow: 'hidden',
                        border: '1px solid var(--color-border, #f1f5f9)',
                        position: 'relative',
                        width: '100%',
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                    }}>
                        {/* Milestone Badge Overlay */}
                        {milestone && (
                            <div className="milestone-badge">{milestone}</div>
                        )}

                        {/* Glass Header */}
                        <div className="feed-glass-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                                <div className="story-circle" style={{ width: '45px', height: '45px', padding: '2px', flexShrink: 0 }}>
                                    <div className="story-inner">
                                        <img
                                            src={photo.expand?.worker?.avatar ? pb.files.getURL(photo.expand.worker, photo.expand.worker.avatar) : `https://ui-avatars.com/api/?name=${photo.expand?.worker?.name || 'MQ'}&background=a4c639&color=fff`}
                                            alt="Avatar"
                                        />
                                    </div>
                                </div>
                                <div style={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: '800', fontSize: '0.95rem', color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {photo.expand?.worker?.name || 'Chef de Chantier'}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <span style={{ color: 'var(--color-primary)', flexShrink: 0 }}>●</span>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {photo.expand?.chantier?.name || 'Chantier'} • {getTimeAgo(photo.created)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                                <button
                                    onClick={() => toggleFavorite(photo.id)}
                                    style={{ background: 'transparent', border: 'none', color: isFav ? '#fbbf24' : 'var(--color-text-muted)', cursor: 'pointer', fontSize: '1.2rem' }}
                                >
                                    {isFav ? <FaStar /> : <FaRegStar />}
                                </button>
                                <button style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '1.1rem' }}>
                                    <FaEllipsisH />
                                </button>
                            </div>
                        </div>

                        {/* Image with Tech Overlay & Click to Lightbox */}
                        <div style={{ width: '100%', position: 'relative', background: '#f8fafc', cursor: 'zoom-in' }} onClick={() => setIndex(i)}>
                            <div className="tech-overlay">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: milestone ? '#a4c639' : '#10b981' }} />
                                    {milestone ? 'ETAPE VALIDÉE' : 'LIVE TERRAIN'}
                                </div>
                                <span style={{ opacity: 0.5 }}>|</span>
                                <span>{new Date(photo.created).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                            </div>

                            {/* Technical Map Pulse */}
                            {(photo.location || photo.caption?.length % 5 === 0) && (
                                <div
                                    className="map-pulse-btn"
                                    title="Voir la localisation technique"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMapPhoto(photo);
                                    }}
                                >
                                    <FaMapMarkerAlt size={14} />
                                </div>
                            )}

                            <img
                                src={photo.url || pb.files.getURL(photo, photo.file)}
                                alt={photo.caption || "Photo de chantier"}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                loading="lazy"
                            />
                        </div>

                        {/* Action Bar */}
                        <div style={{ padding: '15px 20px', display: 'flex', gap: '22px' }}>
                            <button
                                onClick={() => handleLike(photo.id)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '1.4rem',
                                    color: likedPosts[photo.id] ? '#ef4444' : 'var(--color-text)',
                                    transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                }}
                            >
                                {likedPosts[photo.id] ? <FaHeart /> : <FaRegHeart />}
                            </button>
                            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: '1.4rem', color: 'var(--color-text)' }}>
                                <FaComment />
                            </button>
                            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: '1.4rem', color: 'var(--color-text)' }}>
                                <FaShare />
                            </button>
                        </div>

                        {/* Likes & Caption */}
                        <div style={{ padding: '0 20px 20px 20px' }}>
                            <div style={{ fontWeight: '800', fontSize: '0.9rem', marginBottom: '8px', color: 'var(--color-text)' }}>
                                {likes[photo.id]} J'aime
                            </div>
                            <div style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                                <span style={{ fontWeight: '800', marginRight: '8px', color: 'var(--color-text)' }}>{photo.expand?.worker?.name || 'Maison Quinard'}</span>
                                <span style={{ color: 'var(--color-text-muted)' }}>{photo.caption || "Avancement des travaux sur le site."}</span>
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* Lightbox Implementation */}
            <Lightbox
                index={index}
                slides={slides}
                open={index >= 0}
                close={() => setIndex(-1)}
                plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
                animation={{ slideshow: 3000 }}
                slideshow={{ autoplay: false }}
            />

            {/* Diaporama Mode (Force Autoplay) */}
            <Lightbox
                index={0}
                slides={slides}
                open={isDiaporama}
                close={closeDiaporama}
                plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
                slideshow={{ autoplay: true, delay: 3000 }}
            />
            {/* Modal Carte Technique */}
            {mapPhoto && (
                <div className="map-modal-overlay" onClick={() => setMapPhoto(null)}>
                    <div className="map-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="map-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="map-icon-circle"><FaMap /></div>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--color-text-dark)' }}>Localisation Chantier</h4>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Position précise au moment de la prise</p>
                                </div>
                            </div>
                            <button className="close-map-btn" onClick={() => setMapPhoto(null)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="map-container-frame">
                            {parseCoords(mapPhoto.location) ? (
                                <MapContainer
                                    center={parseCoords(mapPhoto.location)}
                                    zoom={18}
                                    style={{ height: '100%', width: '100%' }}
                                    scrollWheelZoom={false}
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <Marker position={parseCoords(mapPhoto.location)}>
                                        <Popup>
                                            <strong>{mapPhoto.caption || "Point de contrôle"}</strong><br />
                                            Maison Quinard - Secteur Sud
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            ) : (
                                <div className="simulated-plan-view">
                                    <FaMapMarkerAlt size={40} color="var(--color-primary)" />
                                    <p>Simulacre de plan technique : <strong>Zone Nord-Est (Jardin)</strong></p>
                                    <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Renseignez des coordonnées (ex: 45.123, 5.456) dans PocketBase pour une vraie carte.</span>
                                </div>
                            )}
                        </div>
                        <div className="map-modal-footer">
                            <span>Coordonnées : {mapPhoto.location || "45.0910, 5.6123 (Demo)"}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* INLINE STYLES FOR PHOTO FEED */}
            <style>{`
                .photo-feed-container { box-sizing: border-box; padding-bottom: 20px; max-width: 100%; overflow-x: hidden; }
                .photo-feed-container * { box-sizing: border-box; max-width: 100%; }
                
                .milestone-badge { position: absolute; top: 15px; right: 15px; background: rgba(0,0,0,0.7); color: #fff; padding: 6px 12px; border-radius: 20px; font-size: 10px; font-weight: 900; letter-spacing: 1px; z-index: 10; backdrop-filter: blur(5px); border: 1px solid rgba(255,255,255,0.2); max-width: 80%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .feed-glass-header { padding: 15px 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(0,0,0,0.05); gap: 10px; }
                
                .tech-overlay { position: absolute; bottom: 15px; left: 15px; background: rgba(0,0,0,0.6); color: #fff; padding: 6px 12px; border-radius: 8px; font-size: 10px; font-weight: 800; display: flex; gap: 8px; align-items: center; backdrop-filter: blur(4px); border: 1px solid rgba(255,255,255,0.1); z-index: 10; pointer-events: none; }
                .map-pulse-btn { position: absolute; bottom: 15px; right: 15px; width: 36px; height: 36px; background: var(--color-primary, #a4c639); color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; box-shadow: 0 4px 15px rgba(164, 198, 57, 0.4); }
                
                /* Map Modal */
                .map-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(5px); }
                .map-modal-content { background: #fff; border-radius: 20px; width: 100%; max-width: 500px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.2); display: flex; flex-direction: column; }
                .map-modal-header { padding: 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; }
                .map-icon-circle { width: 40px; height: 40px; border-radius: 50%; background: #f0fdf4; color: #10b981; display: flex; align-items: center; justify-content: center; font-size: 18px; }
                .close-map-btn { background: transparent; border: none; font-size: 20px; color: #94a3b8; cursor: pointer; }
                .map-container-frame { height: 300px; width: 100%; background: #f8fafc; position: relative; }
                .simulated-plan-view { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 20px; color: #64748b; }
                .map-modal-footer { padding: 15px 20px; background: #f8fafc; font-size: 0.8rem; color: #64748b; font-family: monospace; border-top: 1px solid #f1f5f9; }

                /* DARK MODE OVERRIDES */
                [data-theme="dark"] .feed-card { background: #1e1e1e !important; border-color: rgba(255,255,255,0.08) !important; box-shadow: 0 10px 40px rgba(0,0,0,0.5) !important; }
                [data-theme="dark"] .feed-glass-header { border-bottom-color: rgba(255,255,255,0.05); }
                [data-theme="dark"] .feed-card button { color: #e4e4e7 !important; }
                [data-theme="dark"] .feed-card span { color: #a1a1aa !important; }
                [data-theme="dark"] .feed-card div { color: #e4e4e7; }
                [data-theme="dark"] .feed-card .story-inner { border-color: #1e1e1e; }
                [data-theme="dark"] .map-modal-content { background: #1e1e1e; }
                [data-theme="dark"] .map-modal-header, [data-theme="dark"] .map-modal-footer { border-color: rgba(255,255,255,0.05); background: #121212; }
                [data-theme="dark"] .map-icon-circle { background: rgba(16, 185, 129, 0.2); }
                [data-theme="dark"] .simulated-plan-view { background: #121212; }
            `}</style>
        </div>
    );
};

export default PhotoFeed;
